import React from 'react'
import {useIntl} from 'react-intl'

import AttendanceVisualReport from '../../components/messages/attendance/AttendanceVisualReport'
import AttendanceV2MessageBody from '../../components/messages/attendance/v2/AttendanceV2MessageBody'
import AttendanceV2NameListSection from '../../components/messages/attendance/v2/AttendanceV2NameListSection'
import {AssistantPresentation} from '../assistant/assistantResponseContract'
import {extractNarrativeNameList} from './attendanceNameList'
import {getV2ResultTableData, hasStructuredAttendanceView} from './attendanceV2ResultTable'
import {
  AttendanceV2Payload,
  getCompoundParts,
  isAttendanceResultPayloadKind,
} from './attendanceV2Types'
import {AttendanceChatSendExtras} from './attendanceWizardTypes'
import {hasAttendanceTable, parseAttendanceAnswer} from './parseAttendanceAnswer'
import {stripAttendanceJsonBlock} from './parseAttendanceV2Answer'

type AttendanceMessageBodyProps = {
  text: string
  v2Payload?: AttendanceV2Payload | null
  rawApiAnswer?: string
  attendanceContext?: Record<string, unknown> | null
  /**
   * What the backend said this turn is. Authoritative when set: `text` means prose and nothing
   * else, so an answer with no records draws no report card, no chart and no export controls —
   * which is the whole reason this replaced sniffing the answer text for a marker.
   *
   * Unset only on pre-contract answers, where the legacy table/marker sniffing below still runs.
   */
  presentation?: AssistantPresentation | null
  theme: 'light' | 'dark'
  renderHtmlBody: (text: string, ref?: React.RefObject<HTMLDivElement | null>) => React.ReactNode
  contentRef?: React.RefObject<HTMLDivElement | null>
  onFollowUp?: (query: string, extras?: AttendanceChatSendExtras) => void
}

function tryLegacyTableContent(source: string) {
  const parsed = parseAttendanceAnswer(stripAttendanceJsonBlock(source))
  return hasAttendanceTable(parsed) ? parsed : null
}

const AttendanceMessageBody: React.FC<AttendanceMessageBodyProps> = ({
  text,
  v2Payload,
  rawApiAnswer,
  attendanceContext,
  presentation,
  theme,
  renderHtmlBody,
  contentRef,
  onFollowUp,
}) => {
  const intl = useIntl()

  // The contract already said what this turn is, and it said prose. Everything below this line
  // is inference from the answer text — exactly what drew a report card over "no attendance
  // records found" — so none of it may run once the backend has spoken.
  const contractSaysProseOnly = presentation != null && !v2Payload

  // Prose that enumerates dozens of names ("The students present include A, B, …") gets the same
  // treatment as a packed table cell: the sentence comes out and the names go into a list.
  const renderProse = (text: string) => {
    const nameList = extractNarrativeNameList(text)
    if (!nameList) return <>{renderHtmlBody(text, contentRef)}</>
    return (
      <div>
        {nameList.text ? renderHtmlBody(nameList.text, contentRef) : null}
        <AttendanceV2NameListSection
          names={nameList.names}
          subject={nameList.subject}
          theme={theme}
        />
      </div>
    )
  }

  if (v2Payload) {
    const displayText = stripAttendanceJsonBlock(text).replace(/^error\s*:\s*/i, '') || text
    const {columns, rows, nameLists} = getV2ResultTableData(v2Payload)
    const compoundParts = getCompoundParts(v2Payload)
    // A packed names column that was the whole table leaves no columns behind, but the list it
    // becomes is the table's replacement, not its absence.
    const hasV2Table =
      (columns.length > 0 && rows.length > 0) || nameLists.length > 0
        ? true
        : compoundParts.some((part) => hasStructuredAttendanceView(part))
    // Re-parsing the prose would duplicate what the v2 views already render as tables, and on a
    // contract turn `data` is the only source there is.
    const legacyFallback =
      !hasV2Table && presentation == null ? tryLegacyTableContent(rawApiAnswer || text) : null
    const summary = v2Payload.data?.summary
    const impliedRowCount = summary?.totalStudents ?? summary?.total
    const narrativeImpliesTable =
      /\btable\b/i.test(displayText) ||
      /\bdetails are in the table\b/i.test(displayText) ||
      (impliedRowCount != null && impliedRowCount > 0 && !hasV2Table && !legacyFallback)

    return (
      <div>
        <AttendanceV2MessageBody
          narrative={displayText}
          payload={v2Payload}
          attendanceContext={attendanceContext}
          theme={theme}
          renderHtmlBody={renderHtmlBody}
          narrativeRef={contentRef}
          onFollowUp={onFollowUp ?? (() => {})}
          tableUnavailableHint={Boolean(
            isAttendanceResultPayloadKind(v2Payload.kind) &&
            !hasV2Table &&
            !legacyFallback &&
            narrativeImpliesTable
          )}
        />
        {legacyFallback ? <AttendanceVisualReport content={legacyFallback} theme={theme} /> : null}
      </div>
    )
  }

  const displayText = stripAttendanceJsonBlock(text).replace(/^error\s*:\s*/i, '') || text

  if (contractSaysProseOnly) {
    return renderProse(displayText)
  }

  const parsed = parseAttendanceAnswer(displayText)

  if (hasAttendanceTable(parsed)) {
    return (
      <div>
        {displayText ? renderHtmlBody(displayText, contentRef) : null}
        <AttendanceVisualReport content={parsed} theme={theme} />
      </div>
    )
  }

  const legacyFromRaw = rawApiAnswer ? tryLegacyTableContent(rawApiAnswer) : null
  if (legacyFromRaw) {
    return (
      <div>
        {displayText ? renderHtmlBody(displayText, contentRef) : null}
        <AttendanceVisualReport content={legacyFromRaw} theme={theme} />
      </div>
    )
  }

  if (/\btable\b/i.test(displayText) && !hasAttendanceTable(parsed)) {
    return (
      <div>
        {renderHtmlBody(displayText, contentRef)}
        <div
          className='mt-2'
          style={{
            fontSize: '0.8rem',
            color: theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d',
          }}
        >
          {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.TABLE_UNAVAILABLE'})}
        </div>
      </div>
    )
  }

  return renderProse(displayText)
}

export default AttendanceMessageBody
